import { FC, useEffect, useRef } from 'react';
import { TiClipboard } from 'react-icons/ti';
import { useSelector } from 'react-redux';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import useDualCtaTest from '@core/hooks/useDualCtaTest';
import useProgramCode from '@core/hooks/useProgramCode';
import useStartYourApplicationEditableAsset from '@core/hooks/useStartYourApplicationEditableAsset';
import { selectViewCorrelationIdMap } from '@core/reducers/eventingSlice';
import { selectTestResultById } from '@core/reducers/monarchSlice';
import { PageActions } from '@core/services/newRelic';
import { Program } from '@core/ts/results';
import appendQueryParams from '@core/utils/appendQueryParams';
import cn from '@core/utils/classNames';

import { useGetApplicationCtaData } from '../../hooks/useGetApplicationCtaData';
import styles from './Application.module.css';

type Props = {
  program: Program;
  eventingOverrides?: Record<string, any>;
  isRecommender?: boolean;
  schoolId?: string;
};

// Track viewed programs across all instances
const viewedPrograms = new Set<number>();

const Application: FC<Props> = ({ program, eventingOverrides, isRecommender, schoolId }) => {
  // Use a ref to avoid re-creating the Set on re-renders while sharing across instances
  const viewedProgramsRef = useRef(viewedPrograms);
  const cta = useGetApplicationCtaData({ program });
  const programCode = useProgramCode(program?.id);
  const viewCorrelationIdMap = useSelector(selectViewCorrelationIdMap);
  const flags = useFeatureFlags();
  const { CtaLabelEA, CtaTitleEA, CtaSubtitleEA, ctaLabelCopyEA, dualCtaEnabled, isTestEnabled } =
    useStartYourApplicationEditableAsset(schoolId?.toString() || '');

  const { DualCtaTestComponent, shouldApplyWrapperStyles } = useDualCtaTest(schoolId?.toString() ?? '', programCode, {
    location: eventingOverrides?.location,
  });
  const noPii = useSelector((state) => selectTestResultById(state, 'noPii'));
  const referenceIdQueryParams = useSelector((state: any) => state?.syncLead?.referenceIdQueryParam);

  const { elementViewed, elementClicked } = useElementEvents({
    correlationId: viewCorrelationIdMap[program.id],
    webElement: {
      elementType: 'linkout-cta',
      text: ctaLabelCopyEA || 'Start My Application',
      location: eventingOverrides?.location || 'full-screen',
      htmlId: 'voyager-application-cta',
      name: 'application-cta',
    },
  });

  const onCtaClick = () => {
    elementClicked();
    const finalUrl = appendQueryParams(cta.url, referenceIdQueryParams);
    window.open(finalUrl, '_blank');
    const actionParams = {
      ...flags,
      anonymousId: window._Cohesion.anonymousId,
      correlationId: viewCorrelationIdMap[program.id],
    };
    PageActions.ApplicationCtaClicked(actionParams);
  };

  useEffect(() => {
    if (cta?.label && cta?.url && program?.id) {
      const actionParams = {
        ...flags,
        anonymousId: window._Cohesion.anonymousId,
        correlationId: viewCorrelationIdMap[program.id],
      };

      // Only fire the view event if we haven't tracked this program yet
      if (!viewedProgramsRef.current.has(program.id)) {
        elementViewed();
        PageActions.ApplicationCtaViewed(actionParams);
        viewedProgramsRef.current.add(program.id);
      }
    }
  }, [cta, program?.id]); // Track per program

  if (!cta || cta.url === '') {
    return null;
  }

  if (noPii) {
    return (
      <div>
        <div className={cn(styles.applicationCta, isRecommender && styles.isRecommender)}>
          <span className={styles.applicationCtaHeading}>
            <TiClipboard size="1.5rem" />
            <h2 className={styles.ctaHeading}>
              <CtaTitleEA>Apply Online Today!</CtaTitleEA>
            </h2>
          </span>
          <p>
            <CtaSubtitleEA>
              Online applications are fast, convenient, and let you track your acceptance status. Start yours today!
            </CtaSubtitleEA>
          </p>
        </div>
        <div className={cn(shouldApplyWrapperStyles ? styles.dualCta : styles.singleCta)}>
          {isTestEnabled && dualCtaEnabled && (
            <DualCtaTestComponent schoolId={schoolId || ''} className={cn(styles.ctaButton)} />
          )}
          <button type="button" className={cn(styles.ctaButton, styles.isRecommender)} onClick={onCtaClick}>
            <CtaLabelEA>{cta.label}</CtaLabelEA>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(styles.applicationCta, isRecommender && styles.isRecommender)}>
      <span className={styles.applicationCtaHeading}>
        <TiClipboard size="1.5rem" />
        <h2 className={styles.ctaHeading}>
          <CtaTitleEA>Apply Online Today!</CtaTitleEA>
        </h2>
      </span>
      <p>
        <CtaSubtitleEA>
          Online applications are fast, convenient, and let you track your acceptance status. Start yours today!
        </CtaSubtitleEA>
      </p>
      <div className={cn(shouldApplyWrapperStyles ? styles.dualCta : styles.singleCta)}>
        {isTestEnabled && dualCtaEnabled && (
          <DualCtaTestComponent schoolId={schoolId || ''} className={cn(styles.ctaButton)} />
        )}
        <button type="button" className={cn(styles.ctaButton, styles.isRecommender)} onClick={onCtaClick}>
          <CtaLabelEA>{cta.label}</CtaLabelEA>
        </button>
      </div>
    </div>
  );
};

Application.defaultProps = {
  eventingOverrides: {},
};

export default Application;
